import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Mail, Save, Server, Shield, Send } from 'lucide-react';
import './MasterAdmin.css';

const EmailSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testResult, setTestResult] = useState('');

  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    fromName: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api('/master-admin/email-settings');
      const data = await response.json();
      if (response.ok) {
        setFormData({
          host: data.host || '',
          port: data.port || 587,
          secure: data.secure || false,
          user: data.user || '',
          password: data.password || '', // This will be '********' if configured
          fromName: data.fromName || ''
        });
      }
    } catch (err) {
      setError('Failed to load email settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await api('/master-admin/email-settings', {
        method: 'PUT',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Email settings saved successfully!');
        if (formData.password !== '********') {
          // Update local state to masked password so user knows it saved
          setFormData(prev => ({ ...prev, password: '********' }));
        }
      } else {
        setError(data.message || 'Failed to save settings.');
      }
    } catch (err) {
      setError('Connection error while saving settings.');
    } finally {
      setSaving(false);
      setTimeout(() => setSuccess(''), 5000);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      setTestResult('Please enter an email address for testing.');
      return;
    }
    
    setTesting(true);
    setTestResult('');
    
    try {
      const response = await api('/master-admin/email-settings/test', {
        method: 'POST',
        body: { testEmailAddress }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setTestResult(`✅ ${data.message}`);
      } else {
        setTestResult(`❌ ${data.message}`);
      }
    } catch (err) {
      setTestResult('❌ Connection error while sending test email.');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="ma-loading">Loading Email Settings...</div>;
  }

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <h1>Global Email Settings</h1>
        <p>Configure the SMTP server used for sending global emails (like Forgot Password OTPs).</p>
      </div>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        
        {/* Settings Form */}
        <div style={{ flex: '1 1 500px' }}>
          <div className="ma-card" style={{ padding: '24px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Server size={20} color="#3b82f6" /> SMTP Configuration
            </h2>
            
            {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>{error}</div>}
            {success && <div style={{ background: '#ecfdf5', color: '#059669', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' }}>{success}</div>}
            
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>SMTP Host</label>
                <input 
                  type="text" name="host" value={formData.host} onChange={handleChange}
                  placeholder="e.g. smtp.gmail.com" required
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>SMTP Port</label>
                  <input 
                    type="number" name="port" value={formData.port} onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none' }}
                  />
                </div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', paddingTop: '24px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" name="secure" checked={formData.secure} onChange={handleChange}
                      style={{ width: '16px', height: '16px' }}
                    />
                    Use SSL/TLS (Secure)
                  </label>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>SMTP Username / Email</label>
                <input 
                  type="email" name="user" value={formData.user} onChange={handleChange}
                  placeholder="e.g. support@company.com" required
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>SMTP Password / App Password</label>
                <input 
                  type="password" name="password" value={formData.password} onChange={handleChange}
                  placeholder="Enter new password to change"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none' }}
                />
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  {formData.password === '********' ? 'Password is saved and masked.' : 'Leave blank if you do not want to change the existing password.'}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>From Name (Display Name)</label>
                <input 
                  type="text" name="fromName" value={formData.fromName} onChange={handleChange}
                  placeholder="e.g. CRM Support Team" required
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none' }}
                />
              </div>

              <div style={{ marginTop: '8px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <button 
                  type="submit" disabled={saving}
                  style={{ 
                    padding: '10px 24px', background: '#3b82f6', color: 'white', 
                    border: 'none', borderRadius: '8px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '8px', opacity: saving ? 0.7 : 1
                  }}
                >
                  <Save size={18} /> {saving ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>

            </form>
          </div>
        </div>

        {/* Sidebar Info & Test */}
        <div style={{ flex: '1 1 300px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div className="ma-card" style={{ padding: '24px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Send size={18} color="#10b981" /> Send Test Email
            </h2>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px', lineHeight: 1.5 }}>
              Verify your SMTP configuration by sending a test email. Save your settings first before testing.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input 
                type="email" value={testEmailAddress} onChange={e => setTestEmailAddress(e.target.value)}
                placeholder="test@email.com"
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px' }}
              />
              <button 
                onClick={handleTestEmail} disabled={testing}
                style={{ 
                  padding: '0 16px', background: '#10b981', color: 'white', 
                  border: 'none', borderRadius: '8px', fontWeight: '600', cursor: testing ? 'not-allowed' : 'pointer',
                  opacity: testing ? 0.7 : 1
                }}
              >
                {testing ? 'Sending...' : 'Send'}
              </button>
            </div>
            {testResult && (
              <div style={{ 
                marginTop: '16px', padding: '12px', borderRadius: '8px', fontSize: '13px',
                background: testResult.includes('✅') ? '#ecfdf5' : '#fef2f2',
                color: testResult.includes('✅') ? '#059669' : '#b91c1c',
                border: `1px solid ${testResult.includes('✅') ? '#a7f3d0' : '#fecaca'}`
              }}>
                {testResult}
              </div>
            )}
          </div>

          <div className="ma-card" style={{ padding: '24px', background: '#fffbeb', border: '1px solid #fde68a' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#92400e' }}>
              <Shield size={18} /> Gmail SMTP Guide
            </h2>
            <ul style={{ fontSize: '13px', color: '#92400e', paddingLeft: '16px', margin: 0, display: 'flex', flexDirection: 'column', gap: '8px', lineHeight: 1.5 }}>
              <li><strong>Host:</strong> smtp.gmail.com</li>
              <li><strong>Port:</strong> 587 (Secure: unchecked) or 465 (Secure: checked)</li>
              <li>If using 2-Step Verification on Gmail, you cannot use your regular password.</li>
              <li>Go to Google Account {'>'} Security {'>'} App Passwords.</li>
              <li>Generate a new 16-digit App Password and paste it here.</li>
            </ul>
          </div>

        </div>

      </div>
    </div>
  );
};

export default EmailSettings;
